import React from 'react';
import classNames from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';

import Spinner from '../Spinner/Spinner';

import styles from './Button.module.scss';
import { BackgroundColorStyle, TextStyle } from '../../../../../platforms/web/src/config/themeConfig/stylesInterface';

type Color = 'default' | 'primary' | 'delete' | 'secondary' | 'videoDetailButton' | 'nowBUtton' | 'recentSearchButton' | 'liveEventScheduled';

type Variant =
  | 'contained'
  | 'outlined'
  | 'text'
  | 'danger'
  | 'delete'
  | 'navBar'
  | 'transparent'
  | 'primarySmall'
  | 'dangerText'
  | 'signoutYes'
  | 'signoutNo'
  | 'startWatchingButton'
  | 'navBarExport'
  | 'transparentSmall';

type Props = {
  children?: React.ReactNode;
  label?: string;
  active?: boolean;
  color?: Color;
  fullWidth?: boolean;
  startIcon?: React.ReactElement;
  variant?: Variant;
  // onClick?: MouseEventHandler<HTMLButtonElement>;
  onClick?: any;
  tabIndex?: number;
  size?: 'small' | 'medium' | 'large' | 'exlarge';
  to?: string;
  role?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  busy?: boolean;
  id?: string;
  as?: 'button' | 'a';
  activeClassname?: string;
  halfwith?: boolean;
  style?: BackgroundColorStyle;
  textStyle?: TextStyle;
  target?: string;
  rel?: string;
} & React.AriaAttributes;

const Button: React.FC<Props> = ({
  label,
  children,
  color = 'default',
  startIcon,
  fullWidth = false,
  active = false,
  variant = 'outlined',
  size = 'medium',
  disabled,
  busy,
  type = 'button',
  to,
  as = 'button',
  onClick,
  className,
  activeClassname = '',
  halfwith,
  style,
  textStyle,
  target,
  rel = 'noreferrer',
  ...rest
}: Props) => {
  const buttonClassName = (isActive: boolean) =>
    classNames(styles.button, className, styles[color], styles[variant], {
      [styles.active]: isActive,
      [activeClassname]: isActive,
      [styles.fullWidth]: fullWidth,
      [styles.halfwith]: halfwith,
      [styles.large]: size === 'large',
      [styles.small]: size === 'small',
      [styles.exlarge]: size === 'exlarge',
      [styles.disabled]: disabled,
    });
  const location = useLocation();

  const isLiveTv = (to: string) => {
    return location.pathname.includes('live-tv') && to.includes('live-tv');
  };

  const content = (
    <>
      {startIcon && <div className={styles.startIcon}>{startIcon}</div>}
      {startIcon && label && <span>&nbsp;</span>}
      {
        <span className={classNames(styles.buttonLabel, { [styles.hidden]: busy }) || undefined} style={textStyle}>
          {label}
        </span>
      }
      {children}
      {busy && <Spinner className={styles.centerAbsolute} size={'small'} />}
    </>
  );

  const isExternal = to && /^https?:\/\//.test(to);

  if (to) {
    if (isExternal) {
      return (
        <a href={to} target={target} rel={rel} onClick={onClick} className={buttonClassName(active)} {...rest}>
          {content}
        </a>
      );
    }
    return (
      <NavLink className={({ isActive }) => buttonClassName(isActive || isLiveTv(to))} to={to} {...rest} end>
        {content}
      </NavLink>
    );
  }

  return (
    <button className={buttonClassName(active)} onClick={onClick} type={type} disabled={disabled} aria-disabled={disabled} {...rest} style={style}>
      {content}
    </button>
  );
};
export default Button;
